import { Box, Card, CardContent, CardHeader, Divider, Grid, Skeleton, Typography } from '@mui/material';
import { apiEntities, generic } from 'apis';
import Questionnaire from 'common/Questionnaire';
import { FileProvider, useFileContext } from 'context/FilesV2';
import { formatFiles, downloadFile, uploadFiles, deleteFile, deleteBundle } from 'utils';
import { FunctionsReverse, ProjectTables } from 'enums';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FileGroup } from 'common/FileUpload';
import { Attachment } from '@mui/icons-material';

const FilesUpload = forwardRef(({ fileGroups, id, functionId }, ref) => {
    const { fileGroups: fileGroupsV2, control: filesControl } = useFileContext();

    useImperativeHandle(ref, () => ({
        uploadFiles: async () => {
            await filesControl.uploadFileGroups();
        },
        filebundles: {
            files: fileGroupsV2?.flatMap((group) => group?.bundles),
            id
        }
    }));

    useEffect(() => {
        try {
            const formattedFiles = formatFiles(fileGroups);
            if (formattedFiles !== undefined) {
                filesControl.initialize(formattedFiles);
            } else {
                filesControl.initialize([]);
            }
        } catch (error) {
            console.error('error', error);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileGroups]);
    return (
        <>
            {fileGroupsV2?.map((group, index) => (
                <FileGroup
                    key={group.id}
                    id={group.id}
                    title={group?.title}
                    maxSize={2097152}
                    maxFiles={5}
                    maxBundles={1}
                    fields={group.fields}
                    bundles={group?.bundles}
                    templates={group?.templates}
                />
            ))}
        </>
    );
});

const ClubFiles = ({}, ref) => {
    const { t } = useTranslation();
    const { id } = useParams();

    const filesQuery = useQuery(['files', id], async ({ queryKey }) => {
        const [, id] = queryKey;

        const getFilesRequest = {
            DataKey: 'Organization',
            RecordId: null,
            RecordGuid: id,
            ProjectTableId: ProjectTables.Organization
        };
        const files = await generic.getFiles(getFilesRequest);
        return files;
    });

    return {
        loading: <Skeleton />,
        success: !filesQuery?.data ? null : (
            <Card elevation={3} sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Attachment sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {t('Attachments')}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />

                    <FileProvider
                        downloadFile={(fileId, type) => downloadFile(fileId, type)}
                        uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
                        onDeleteBundle={async (bundleId) =>
                            await deleteBundle(bundleId, ProjectTables.Organization, null, id === '0' ? null : id, null)
                        }
                        onDeleteFile={async (fileId) =>
                            await deleteFile(fileId, ProjectTables.Organization, null, id === '0' ? null : id, null)
                        }
                    >
                        <FilesUpload fileGroups={filesQuery?.data} ref={ref} id={id} />
                    </FileProvider>
                </CardContent>
            </Card>
        ),
        error: <>Error fetching required attachments</>
    }[filesQuery?.status];
};

export default forwardRef(ClubFiles);
